<template>
  <div v-if="jobSettings">
    <!-- Help Text -->
    <div v-if="jobSettings.helpText" class="alert alert-info mb-3">
      <small>{{ jobSettings.helpText }}</small>
    </div>

    <!-- Schedule From -->
    <PFRow
      v-if="jobSettings.showScheduleFrom"
      :label="$pgettext('email_schedules', 'Schedule From')">
      <VueCtkDateTimePicker
        v-model="form.schedule_from"
        format="YYYY-MM-DD"
        formatted="DD/MM/YYYY"
        :only-date="true"
        :no-button="true"
        :label="$pgettext('email_schedules', 'Select start date')"/>
      <small class="text-muted">
        <translate translate-context="email_schedules">
          Start date for this schedule
        </translate>
      </small>
    </PFRow>

    <!-- Schedule To -->
    <PFRow
      v-if="jobSettings.showScheduleTo"
      :label="$pgettext('email_schedules', 'Schedule To')">
      <VueCtkDateTimePicker
        v-model="form.schedule_to"
        format="YYYY-MM-DD"
        formatted="DD/MM/YYYY"
        :only-date="true"
        :no-button="true"
        :label="$pgettext('email_schedules', 'Select end date')"/>
      <small class="text-muted">
        <translate translate-context="email_schedules">
          End date for this schedule (optional, leave empty for indefinite)
        </translate>
      </small>
    </PFRow>

    <!-- Send Time -->
    <PFRow
      v-if="jobSettings.showSendTime"
      :label="$pgettext('email_schedules', 'Send Time')">
      <vue-timepicker
        v-model="form.send_time"
        format="HH:mm"
        :hour-range="[[0, 23]]"
        :minute-interval="15"/>
      <small class="text-muted">
        <translate translate-context="email_schedules">
          Time of day when emails should be sent (your local timezone)
        </translate>
      </small>
    </PFRow>

    <!-- Send Every -->
    <PFRow
      v-if="jobSettings.showSendEvery"
      :label="jobSettings.sendEveryLabel || $pgettext('email_schedules', 'Send Every')">
      <CInput
        v-model.number="form.send_every"
        type="number"
        min="1"
        :readonly="jobSettings.sendEveryReadonly"
        :placeholder="jobSettings.sendEveryPlaceholder || $pgettext('email_schedules', 'Hours')"/>
      <small v-if="jobSettings.sendEveryHelpText" class="text-muted">
        {{ jobSettings.sendEveryHelpText }}
      </small>
      <small v-else class="text-muted">
        <translate translate-context="email_schedules">
          Frequency in hours between sending emails
        </translate>
      </small>
    </PFRow>

    <!-- Custom Parameters -->
    <PFRow
      v-for="param in jobSettings.customParameters"
      :key="param.key"
      :label="param.name">
      <!-- Number input -->
      <CInput
        v-if="param.type === 'number'"
        type="number"
        v-model.number="form.params[param.key]"
        :placeholder="param.default ? String(param.default) : ''"/>

      <!-- Text input -->
      <CInput
        v-else-if="param.type === 'text'"
        type="text"
        v-model="form.params[param.key]"
        :placeholder="param.default || ''"/>

      <!-- Boolean switch -->
      <div v-else-if="param.type === 'boolean'" class="d-flex align-items-center">
        <CSwitch
          color="primary"
          variant="3d"
          size="sm"
          :checked.sync="form.params[param.key]"/>
        <label class="ml-2 mb-0">{{ param.name }}</label>
      </div>

      <!-- Select dropdown -->
      <PSelectKeyValue
        v-else-if="param.type === 'select'"
        :options="param.options"
        :value.sync="form.params[param.key]"
        label="label"
        track-by="value"/>

      <small v-if="param.hint" class="text-muted">{{ param.hint }}</small>
    </PFRow>
  </div>
</template>

<script>
import PFRow from '@/domain/core/components/PFRow.vue'
import PSelectKeyValue from '@/domain/core/components/PSelectKeyValue.vue'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import VueTimepicker from 'vue2-timepicker'
import 'vue2-timepicker/dist/VueTimepicker.css'
import { getJobSettingsConfig } from '@/domain/core/constant/emailJobTypes'

export default {
  name: 'JobSettingsFields',
  components: {
    PFRow,
    PSelectKeyValue,
    VueCtkDateTimePicker,
    VueTimepicker
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    jobType: {
      type: String,
      required: true
    }
  },
  computed: {
    jobSettings() {
      if (!this.jobType) return null
      return getJobSettingsConfig(this.jobType)
    }
  },
  watch: {
    jobType: {
      immediate: true,
      handler(newJobType) {
        this.initializeJobParameters(newJobType)
      }
    }
  },
  methods: {
    initializeJobParameters(jobType) {
      if (!jobType) return

      const settings = getJobSettingsConfig(jobType)
      if (!settings) return

      // Initialize params if not exists
      if (!this.form.params) {
        this.$set(this.form, 'params', {})
      }

      // Set default values for custom parameters
      if (settings.customParameters) {
        settings.customParameters.forEach(param => {
          if (this.form.params[param.key] === undefined && param.default !== undefined) {
            this.$set(this.form.params, param.key, param.default)
          }
        })
      }

      // Set default send_every if needed
      if (settings.showSendEvery && !this.form.send_every && settings.sendEveryDefault) {
        this.$set(this.form, 'send_every', settings.sendEveryDefault)
      }
    }
  }
}
</script>

<style scoped>
.alert {
  border-radius: 0.25rem;
}
</style>
